import React, { useState, useEffect, useRef, FormEvent } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/promoCodePage.module.scss';
import tableStyles from '../css/table.module.scss';
import { useAuth } from '../context/AuthContext';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import { createPortal } from 'react-dom';
import { formatStatus } from '../helpers/functions';
import Pagination from '../components/pagination';
import CustomTextInput from '../components/customTextInput';
import CustomDropdownSearch from '../components/customDropdownSearch';
import CustomCalendar from '../components/customCalendar';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { toast } from 'react-toastify';
import { PlanInterface } from '../helpers/objects/planObject';
import PromoCodeAddedModal from '../components/promoCodeAddedModal';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import PromoCodeDeletingModal from '../components/promoCodeDeletingModal';

interface ValidityPeriod {
  from: string;
  to: string;
}

interface UsageHistory {
  discountedAmount: number;
  finalPricePayed: number;
  userEmail: string;
  planId: string;
  _id: string;
  createdAt: string;
}

interface PromoCode {
  validityPeriod: ValidityPeriod;
  _id: string;
  code: string;
  discountType: number;
  promoCodeType: string;
  plans: PlanInterface[];
  usagePerUser: number;
  globalUsageLimit: number;
  isActive: boolean;
  usageHistory: UsageHistory[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  isDeleted: boolean;
  deletedAt: Date;
}

interface PromoCodeData {
  data: PromoCode[];
  totalNumberOfUsages: number;
  totalRevenue: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const PromoCodePage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [isActionOpen, setIsActionOpen] = useState<number | string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [view, setView] = useState<'new-promo-code' | 'promo-codes' | 'analytics' | 'edit-promo-code'>('new-promo-code');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [promoCodesSearchTerm, setPromoCodesSearchTerm] = useState<string>('');
  const [fetchedData, setFetchedData] = useState<PromoCodeData>({ data: [], totalNumberOfUsages: 0, totalRevenue: 0, pagination: { currentPage: 1, totalPages: 1, totalCount: 0, limit: 10, hasNextPage: false, hasPrevPage: false } });
  const [page, setPage] = useState<number>(1);
  const [successfulModalOpen, setSuccessfulModalOpen] = useState<boolean>(false);
  const [bulkCreation, setBulkCreation] = useState(false);
  const [bulkCreationAmount, setBulkCreationAmount] = useState<number>(2);
  const [selectedPromoCodeIds, setSelectedPromoCodeIds] = useState<string[]>([]);
  const [deletePromoCodesModalOpen, setDeletePromoCodesModalOpen] = useState<boolean>(false);

  const [formErrors, setFormErrors] = useState({
    promoCodeName: '',
    discountType: '',
    validityPeriod: '',
    codeType: '',
    restrictionsPerUser: '',
    restrictionsGlobal: '',
    plans: '',
  });

  const allItemsSelected = fetchedData.data.length > 0 && fetchedData.data.every((item) => selectedPromoCodeIds.includes(item._id));

  const [dateValues, setDateValues] = useState<any[]>([null, null]);

  const [promoCodeName, setPromoCodeName] = useState<string>('');

  const [promoCodeRestrictionsPerUser, setPromoCodeRestrictionsPerUser] = useState<string>('');
  const [promoCodeRestrictionsGlobal, setPromoCodeRestrictionsGlobal] = useState<string>('');

  const [selectingPlansOpen, setSelectingPlansOpen] = useState<boolean>(false);
  const selectingPlansDropRef = useRef<HTMLDivElement>(null);

  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [allPlans, setAllPlans] = useState<{ value: string; name: string }[]>([]);
  const [availablePlansOptions, setAvailablePlansOptions] = useState<{ value: string; name: string }[]>([]);

  const [codeType, setCodeType] = useState<{ value: string; label: string }>({ value: '', label: '' });
  const [codeTypeOpen, setCodeTypeOpen] = useState<boolean>(false);

  const [discountType, setDiscountType] = useState<{ value: string; label: string }>({ value: '', label: '' });
  const [discountTypeOpen, setDiscountTypeOpen] = useState<boolean>(false);
  const [sortByStatus, setSortByStatus] = useState<{ value: string; label: string }>({ value: 'all', label: t('promoCode.labels.all') });
  const [filterSortByStatusOpen, setFilterSortByStatusOpen] = useState<boolean>(false);
  const [sortByType, setSortByType] = useState<{ value: string; label: string }>({ value: 'all', label: t('promoCode.labels.all') });
  const [filterSortByTypeOpen, setFilterSortByTypeOpen] = useState<boolean>(false);
  const filterSortByStatusDropRef = useRef<HTMLDivElement>(null);
  const filterSortByTypeDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const discountTypeDropRef = useRef<HTMLDivElement>(null);
  const codeTypeDropRef = useRef<HTMLDivElement>(null);
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);

  const dateRangeRef = useRef(null);

  const handleActionClick = (id: number | string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(actionDropdownRef, () => {
    setIsActionOpen(null);
  });

  const formatDateForBackend = (date: any) => {
    if (!date) {
      return null;
    }
    // If date has toDate method (from date picker library), use it
    if (date.toDate) {
      const d = date.toDate();
      return d.toISOString();
    }
    // If date is already a Date object, use it directly
    if (date instanceof Date) {
      return date.toISOString();
    }
    return null;
  };

  const handleDateChange = (value: any) => {
    setDateValues(value);
  };

  const handleDateOnCancel = () => {
    setDateValues([null, null]);
  };

  const handleCSVExport = async () => {
    try {
      const response = await fetch(`${EnvPaths.SERVER_URL}/admin/download-promo-codes-csv`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `promo_codes.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    setSortByStatus((prev) => ({ ...prev, label: t('promoCode.labels.all') }));
    setSortByType((prev) => ({ ...prev, label: t('promoCode.labels.all') }));
  }, [t, i18next]);

  useClickAway(selectingPlansDropRef, () => {
    setSelectingPlansOpen(false);
  });

  useClickAway(discountTypeDropRef, () => {
    setDiscountTypeOpen(false);
  });

  const handleDiscountTypeChange = (item: { value: string; label: string }) => {
    setDiscountType(item);
    setDiscountTypeOpen(false);
  };

  const discountTypeOptions = [
    { value: '5', label: '5%' },
    { value: '10', label: '10%' },
    { value: '15', label: '15%' },
    { value: '20', label: '20%' },
    { value: '25', label: '25%' },
    { value: '100', label: '100%' },
  ];

  useClickAway(codeTypeDropRef, () => {
    setCodeTypeOpen(false);
  });

  const handleCodeTypeChange = (item: { value: string; label: string }) => {
    setCodeType(item);
    setCodeTypeOpen(false);
  };

  const codeTypeOptions = [
    { value: 'goodwill', label: t('promoCode.labels.goodwill') },
    { value: 'welcome', label: t('promoCode.labels.welcome') },
    { value: 'vip', label: t('promoCode.labels.vip') },
    { value: 'seasonal', label: t('promoCode.labels.seasonal') },
  ];

  useClickAway(filterSortByStatusDropRef, () => {
    setFilterSortByStatusOpen(false);
  });

  const handleSortByStatusChange = (item: { value: string; label: string }) => {
    setPage(1);
    setSortByStatus(item);
    setFilterSortByStatusOpen(false);
  };

  const handlePromoCodesSearch = (value: string) => {
    setPage(1);
    setPromoCodesSearchTerm(value);
  };

  const filterSortByStatusOptions = [
    { value: 'all', label: t('promoCode.labels.all') },
    { value: 'active', label: t('promoCode.labels.active') },
    { value: 'deactivated', label: t('promoCode.labels.deactivated') },
  ];

  useClickAway(filterSortByTypeDropRef, () => {
    setFilterSortByTypeOpen(false);
  });

  const handleSortByTypeChange = (item: { value: string; label: string }) => {
    setPage(1);
    setSortByType(item);
    setFilterSortByTypeOpen(false);
  };

  const filterSortByTypeOptions = [
    { value: 'all', label: t('promoCode.labels.all') },
    { value: 'goodwill', label: t('promoCode.labels.goodwill') },
    { value: 'welcome', label: t('promoCode.labels.welcome') },
    { value: 'vip', label: t('promoCode.labels.vip') },
    { value: 'seasonal', label: t('promoCode.labels.seasonal') },
  ];

  const onPrevious = () => {
    setPage(page - 1);
  };

  const onNext = () => {
    setPage(page + 1);
  };

  const handleChangeSelectedPlans = (item: string) => {
    if (selectedPlans.includes(item)) {
      setSelectedPlans(selectedPlans.filter((plan) => plan !== item));
    } else {
      setSelectedPlans([...selectedPlans, item]);
    }
  };

  const handleSearchAvailablePlans = (searchTerm: string) => {
    if (searchTerm === '') {
      setAvailablePlansOptions(allPlans);
    } else {
      setAvailablePlansOptions(allPlans.filter((plan) => plan.name.toLowerCase().includes(searchTerm.toLowerCase())));
    }
  };

  const handleAddPromoCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateAllFields()) {
      return;
    }

    const theSelectedPlans = selectedPlans.map((plan) => {
      return allPlans.find((p) => p.name === plan)?.value;
    });

    if (theSelectedPlans.length === 0 || (promoCodeName === '' && !bulkCreation) || discountType.value === '' || codeType.value === '' || dateValues[0] === null || dateValues[1] === null) {
      toast.error(t('promoCode.toastError'));
      return;
    }

    const data = {
      code: promoCodeName,
      promoCodeType: codeType.value,
      discountType: Number(discountType.value),
      validityPeriod: {
        from: formatDateForBackend(dateValues[0]),
        to: formatDateForBackend(dateValues[1]),
      },
      plans: theSelectedPlans,
      usagePerUser: Number(promoCodeRestrictionsPerUser) ?? 1,
      globalUsageLimit: Number(promoCodeRestrictionsGlobal) ?? 1,
      bulkCreation,
      bulkCreationAmount,
    };

    toast.loading(t('promoCode.toastLoading'));
    await axios
      .post(`${EnvPaths.SERVER_URL}/admin/create-promo-code`, data, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then(() => {
        toast.dismiss();
        toast.success(t('promoCode.toastSuccess'));
        setSuccessfulModalOpen(true);
        setView('promo-codes');
        setSelectedPlans([]);
        setPromoCodeName('');
        setPromoCodeRestrictionsGlobal('');
        setPromoCodeRestrictionsPerUser('');
        setDateValues([null, null]);
        setDiscountType({ value: '', label: '' });
        setCodeType({ value: '', label: '' });
        getPromoCodesList();
      })
      .catch((err) => {
        toast.dismiss();
        if (err.response.status === 403 || err.response.status === 401) {
          toast.error(t('promoCode.toastError401'));
        } else if (err.response.status === 409) {
          toast.error(t('promoCode.toastError409'));
        } else {
          toast.error(t('promoCode.toastError500'));
        }
        return;
      });
  };

  const handleBulkDelete = () => {
    toast.loading(t('promoCode.deletingPromoCode'));
    axios
      .delete(`${EnvPaths.SERVER_URL}/admin/bulk-delete-promo-codes`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        data: {
          ids: selectedPromoCodeIds,
        },
      })
      .then(() => {
        toast.dismiss();
        toast.success(t('promoCode.deletingSuccess'));
        setSelectedPromoCodeIds([]);
        getPromoCodesList();
        setDeletePromoCodesModalOpen(false);
      })
      .catch((err) => {
        toast.dismiss();
        if (err.response.status === 403 || err.response.status === 401) {
          toast.error(t('promoCode.toastError401'));
        } else {
          toast.error(t('promoCode.toastError500'));
        }
        return;
      });
  };

  const handleBulkChangeState = () => {
    toast.loading(t('promoCode.toastLoading'));
    axios
      .put(
        `${EnvPaths.SERVER_URL}/admin/bulk-change-state-promo-codes`,
        {
          ids: selectedPromoCodeIds,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('promoCode.handleChangePromoCodeStatus.toastSuccess'));
        setSelectedPromoCodeIds([]);
        getPromoCodesList();
      })
      .catch((err) => {
        toast.dismiss();
        if (err.response.status === 403 || err.response.status === 401) {
          toast.error(t('promoCode.handleChangePromoCodeStatus.toastError401'));
        } else {
          toast.error(t('promoCode.handleChangePromoCodeStatus.toastError500'));
        }
        return;
      });
  };

  const formatPlanName = (singleFlight: boolean, family: boolean, periodInMonths?: number) => {
    try {
      const planType = family ? t('promoCode.formatPlanName.familyPlan') : t('promoCode.formatPlanName.soloPlan');

      const name = singleFlight ? t('promoCode.formatPlanName.single') + ` - ${planType}` : `${periodInMonths}` + t('promoCode.formatPlanName.month') + ` - ${planType}`;
      return name;
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  const getProductList = async () => {
    setIsLoading(true);
    await axios
      .get(`${EnvPaths.SERVER_URL}/skycop/get-local-product-list`)
      .then((response) => {
        const filteredData = response.data.map((item: { _id: string; product: { name: string; singleFlight: boolean; family: boolean; periodInMonths?: number } }) => {
          const { singleFlight, family, periodInMonths } = item.product;

          const name = formatPlanName(singleFlight, family, periodInMonths);

          return {
            value: item?._id ?? '',
            name: name ?? '',
          };
        });
        setAllPlans(filteredData);
        setAvailablePlansOptions(filteredData);
      })
      .catch((error) => {
        console.error(error);
      });
    setIsLoading(false);
  };

  const getPromoCodesList = async () => {
    setIsLoading(true);
    await axios
      .get(`${EnvPaths.SERVER_URL}/admin/get-promo-codes?page=${page}&q=${promoCodesSearchTerm}&type=${sortByType.value}&status=${sortByStatus.value}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((response) => {
        const { data, totalNumberOfUsages, totalRevenue, pagination } = response.data;
        setFetchedData({
          data,
          totalNumberOfUsages,
          totalRevenue,
          pagination,
        });
      })
      .catch((error) => {
        console.error(error);
      });
    setIsLoading(false);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const hours = String(d.getHours()).padStart(2, '0'); // Local hours
    const minutes = String(d.getMinutes()).padStart(2, '0'); // Local minutes
    const seconds = String(d.getSeconds()).padStart(2, '0'); // Local seconds
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const year = d.getFullYear(); // Local year
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Local month
    const day = String(d.getDate()).padStart(2, '0'); // Local day
    return `${year}/${month}/${day}`;
  };

  const handleChangePromoCodeStatus = (id: string, statusTo: boolean) => {
    toast.loading(t('promoCode.handleChangePromoCodeStatus.toastLoading'));
    axios
      .patch(
        `${EnvPaths.SERVER_URL}/admin/change-promo-code-status/${id}`,
        {
          isActive: statusTo,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('promoCode.handleChangePromoCodeStatus.toastSuccess'));
        getPromoCodesList();
      })
      .catch((error) => {
        toast.dismiss();
        console.error(error);
        if (error.response.status === 403 || error.response.status === 401) {
          toast.error(t('promoCode.handleChangePromoCodeStatus.toastError401'));
        } else {
          toast.error(t('promoCode.handleChangePromoCodeStatus.toastError500'));
        }
      });
  };

  useEffect(() => {
    getProductList();
    getPromoCodesList();
  }, [user]);

  useEffect(() => {
    getPromoCodesList();
  }, [page, promoCodesSearchTerm, sortByStatus, sortByType]);

  const handleEditPromoCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (editingPromoCode === null) return;

    const theSelectedPlans = selectedPlans.map((plan) => {
      return allPlans.find((p) => p.name === plan)?.value;
    });

    if (theSelectedPlans.length === 0 || editingPromoCode?.code === '' || !editingPromoCode.discountType || !editingPromoCode.promoCodeType || dateValues[0] === null || dateValues[1] === null) {
      toast.error(t('promoCode.handleEditPromoCode.toastError'));
      return;
    }

    const data = {
      code: editingPromoCode.code,
      promoCodeType: editingPromoCode.promoCodeType,
      discountType: Number(editingPromoCode.discountType),
      validityPeriod: {
        from: formatDateForBackend(dateValues[0]),
        to: formatDateForBackend(dateValues[1]),
      },
      plans: theSelectedPlans,
      usagePerUser: Number(editingPromoCode?.usagePerUser) ?? 1,
      globalUsageLimit: Number(editingPromoCode?.globalUsageLimit) ?? 1,
    };
    toast.loading(t('promoCode.handleEditPromoCode.toastLoading'));
    await axios
      .put(`${EnvPaths.SERVER_URL}/admin/update-promo-code/${editingPromoCode._id}`, data, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then(() => {
        toast.dismiss();
        toast.success(t('promoCode.handleEditPromoCode.toastSuccess'));
        setView('promo-codes');
        setEditingPromoCode(null);
        setSelectedPlans([]);
        setPromoCodeName('');
        setPromoCodeRestrictionsGlobal('');
        setPromoCodeRestrictionsPerUser('');
        setDateValues([null, null]);
        setDiscountType({ value: '', label: '' });
        setCodeType({ value: '', label: '' });
        getPromoCodesList();
      })
      .catch((err) => {
        toast.dismiss();
        if (err.response.status === 403 || err.response.status === 401) {
          toast.error(t('promoCode.handleEditPromoCode.toastError401'));
        } else if (err.response.status === 409) {
          toast.error(t('promoCode.handleEditPromoCode.toastError409'));
        } else {
          toast.error(t('promoCode.handleEditPromoCode.toastError500'));
        }
        return;
      });
  };

  const handleStartEditCode = (item: PromoCode) => {
    const theSelectedPlans = item.plans.map((plan) => availablePlansOptions.find((p) => p.value === plan._id)).filter(Boolean);

    setEditingPromoCode(item);
    setSelectedPlans(theSelectedPlans.map((plan) => plan?.name ?? ''));

    if (item.validityPeriod.from && item.validityPeriod.to) {
      setDateValues([new Date(item.validityPeriod.from), new Date(item.validityPeriod.to)]);
    }

    setView('edit-promo-code');
  };

  const validateField = (field: string, value: any) => {
    let error = '';

    switch (field) {
      case 'promoCodeName':
        if (!bulkCreation && (!value || value.trim().length === 0)) {
          error = t('promoCode.validation.promoCodeNameRequired');
        }
        break;
      case 'discountType':
        if (!value || value === '') {
          error = t('promoCode.validation.discountTypeRequired');
        }
        break;
      case 'validityPeriod':
        if (!dateValues[0] || !dateValues[1]) {
          error = t('promoCode.validation.validityPeriodRequired');
        }
        break;
      case 'codeType':
        if (!value || value === '') {
          error = t('promoCode.validation.codeTypeRequired');
        }
        break;
      case 'restrictionsPerUser':
        if (!value || value === '') {
          error = t('promoCode.validation.restrictionsPerUserRequired');
        }
        break;
      case 'restrictionsGlobal':
        if (!value || value === '') {
          error = t('promoCode.validation.restrictionsGlobalRequired');
        }
        break;
      case 'plans':
        if (!value || value.length === 0) {
          error = t('promoCode.validation.plansRequired');
        }
        break;
    }

    setFormErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateAllFields = (): boolean => {
    const errors = {
      promoCodeName: validateField('promoCodeName', promoCodeName),
      discountType: validateField('discountType', discountType.value),
      validityPeriod: validateField('validityPeriod', dateValues),
      codeType: validateField('codeType', codeType.value),
      restrictionsPerUser: validateField('restrictionsPerUser', promoCodeRestrictionsPerUser),
      restrictionsGlobal: validateField('restrictionsGlobal', promoCodeRestrictionsGlobal),
      plans: validateField('plans', selectedPlans),
    };

    const hasErrors = Object.values(errors).some((error) => error !== '');

    if (hasErrors) {
      toast.error(t('promoCode.validation.fillAllFields'));
    }

    return !hasErrors;
  };

  useEffect(() => {
    if (bulkCreation) {
      setFormErrors((prev) => ({ ...prev, promoCodeName: '' }));
    }
  }, [bulkCreation]);

  return (
    <main className={styles.pageContainer}>
      <div className={globalPageStyle.pageFlexContainer} style={{ height: '100%' }}>
        {/*header - tabs*/}
        <div className={globalPageStyle.tabs}>
          <div
            className={`${globalPageStyle.tab} ${view === 'new-promo-code' ? globalPageStyle.active : ''}`}
            onClick={() => {
              setView('new-promo-code');
              setEditingPromoCode(null);
              setDateValues([null, null]);
              setIsActionOpen(null);
            }}
          >
            {t('promoCode.newPromoCode')}
          </div>
          <div
            className={`${globalPageStyle.tab} ${view === 'promo-codes' ? globalPageStyle.active : ''}`}
            onClick={() => {
              setView('promo-codes');
              setEditingPromoCode(null);
              setDateValues([null, null]);
              setIsActionOpen(null);
            }}
          >
            {t('promoCode.promoCodes')}
          </div>
          <div
            className={`${globalPageStyle.tab} ${view === 'analytics' ? globalPageStyle.active : ''}`}
            onClick={() => {
              setView('analytics');
              setEditingPromoCode(null);
              setDateValues([null, null]);
              setIsActionOpen(null);
            }}
          >
            {t('promoCode.analytics')}
          </div>
          {editingPromoCode && (
            <div
              className={`${globalPageStyle.tab} ${view === 'edit-promo-code' ? globalPageStyle.active : ''}`}
              onClick={() => {
                setView('edit-promo-code');
                setIsActionOpen(null);
              }}
            >
              {t('promoCode.editPromoCode')}
            </div>
          )}
        </div>
        {/*header - tabs*/}

        {user && (user.role === 'admin' || user.role === 'skycop') ? (
          isLoading ? (
            <div className="loader" />
          ) : (
            <div style={{ overflow: 'auto' }}>
              <div className={styles.pageContent}>
                {view === 'new-promo-code' && (
                  <>
                    <form
                      className={styles.newPromoCodeContainer}
                      onSubmit={(e) => {
                        handleAddPromoCode(e);
                      }}
                    >
                      <p className={styles.newPromoCodeTitle}>{t('promoCode.createNewPromoCode')}</p>
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.name')}</p>
                          <CustomTextInput
                            placeholder="e.g., 'welcome10'"
                            type="text"
                            id="name"
                            value={promoCodeName}
                            onChange={(e) => {
                              setPromoCodeName(e.target.value);
                              validateField('promoCodeName', e.target.value);
                            }}
                            className={styles.inputClass}
                            disabled={bulkCreation}
                            error={formErrors.promoCodeName}
                          />
                          {formErrors.promoCodeName && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.promoCodeName}
                            </span>
                          )}
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.discountType')}</p>
                          <CustomDropdown
                            ref={discountTypeDropRef}
                            isOpen={discountTypeOpen}
                            onToggle={() => setDiscountTypeOpen(!discountTypeOpen)}
                            options={discountTypeOptions}
                            value={discountType}
                            onChange={(item) => {
                              handleDiscountTypeChange(item);
                              validateField('discountType', item.value);
                            }}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.chooseValue')}
                            error={formErrors.discountType}
                          />
                          {formErrors.discountType && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.discountType}
                            </span>
                          )}
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.validityPeriod')}</p>
                          {/* <CustomDropdown
                            ref={validityDropRef}
                            isOpen={validityOpen}
                            onToggle={() => setValidityOpen(!validityOpen)}
                            options={validityOptions}
                            value={validity}
                            onChange={handleValidityChange}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder="Choose period"
                          /> */}
                          {/*date range - calendar*/}
                          <CustomCalendar dateValues={dateValues} handleDateChange={handleDateChange} onCancel={handleDateOnCancel} ref={dateRangeRef} fullWidth fullHeight showHours />
                          {formErrors.validityPeriod && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.validityPeriod}
                            </span>
                          )}
                          {/*date range - calendar*/}
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.codeType')}</p>
                          <CustomDropdown
                            ref={codeTypeDropRef}
                            isOpen={codeTypeOpen}
                            onToggle={() => setCodeTypeOpen(!codeTypeOpen)}
                            options={codeTypeOptions}
                            value={codeType}
                            onChange={(item) => {
                              handleCodeTypeChange(item);
                              validateField('codeType', item.value);
                            }}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.chooseCodeType')}
                            error={formErrors.codeType}
                          />
                          {formErrors.codeType && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.codeType}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* grid container*/}
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.restrictionsPerUser')} *</p>
                          <CustomTextInput
                            placeholder="e.g., '1'"
                            type="number"
                            min={1}
                            id="restrictionsperuser"
                            value={promoCodeRestrictionsPerUser}
                            onChange={(e) => {
                              if (e.target.value !== '' && Number(e.target.value) < 1) {
                                e.target.value = '1';
                                setPromoCodeRestrictionsPerUser('1');
                              } else {
                                setPromoCodeRestrictionsPerUser(e.target.value);
                              }
                              validateField('restrictionsPerUser', e.target.value);
                            }}
                            className={styles.inputClass}
                            error={formErrors.restrictionsPerUser}
                          />
                          {formErrors.restrictionsPerUser && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.restrictionsPerUser}
                            </span>
                          )}
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.restrictionsPerGlobalUsage')} *</p>
                          <CustomTextInput
                            placeholder="e.g., '500'"
                            type="number"
                            min={1}
                            id="restrictionsglobal"
                            value={promoCodeRestrictionsGlobal}
                            onChange={(e) => {
                              if (e.target.value !== '' && Number(e.target.value) < 1) {
                                e.target.value = '1';
                                setPromoCodeRestrictionsGlobal('1');
                              } else {
                                setPromoCodeRestrictionsGlobal(e.target.value);
                              }
                              validateField('restrictionsGlobal', e.target.value);
                            }}
                            className={styles.inputClass}
                            error={formErrors.restrictionsGlobal}
                          />
                          {formErrors.restrictionsGlobal && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.restrictionsGlobal}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* grid container*/}
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.plans')}</p>
                          <CustomDropdownSearch
                            ref={selectingPlansDropRef}
                            isOpen={selectingPlansOpen}
                            onToggle={() => setSelectingPlansOpen(!selectingPlansOpen)}
                            options={availablePlansOptions.map((plan) => plan.name)}
                            value={selectedPlans}
                            onChange={(item) => {
                              handleChangeSelectedPlans(item);
                              validateField('plans', selectedPlans.includes(item) ? selectedPlans.filter((p) => p !== item) : [...selectedPlans, item]);
                            }}
                            onSearch={handleSearchAvailablePlans}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.choosePlan')}
                            error={formErrors.plans}
                          />
                          {formErrors.plans && (
                            <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                              {formErrors.plans}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* grid container*/}

                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <label htmlFor="terms-agree" className="fancy-checkbox flex items-start gap-2">
                            <input type="checkbox" id="terms-agree" checked={bulkCreation} onChange={(e) => setBulkCreation(e.target.checked)} />

                            <span className="box" />

                            <span>{t('promoCode.bulkCreation')}</span>
                          </label>

                          {bulkCreation && (
                            <div className={styles.gridItem}>
                              <p className={styles.gridItemLabel}>{t('promoCode.numberOfCodes')}</p>
                              <CustomTextInput
                                type="number"
                                id="bulkNumber"
                                defaultValue={bulkCreationAmount}
                                onChange={(e) => {
                                  const value = e.target.valueAsNumber;
                                  if (!isNaN(value)) {
                                    setBulkCreationAmount(Math.max(2, Math.min(Math.floor(value), 999)));
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = e.target.valueAsNumber;
                                  if (isNaN(value) || value < 2) {
                                    e.target.value = '2';
                                    setBulkCreationAmount(2);
                                  }
                                }}
                                min={2}
                                max={999}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <button type="submit" className="primaryButton fit">
                        {t('promoCode.createPromoCode')}
                      </button>
                    </form>
                  </>
                )}
                {view === 'promo-codes' && (
                  <>
                    {/*filters*/}
                    <div className={styles.header}>
                      <div className={styles.headerLeft}>
                        <div className={styles.flexRow}>
                          {selectedPromoCodeIds.length === 0 ? (
                            <>
                              <CustomSearchInput defaultValue={promoCodesSearchTerm} onSearch={handlePromoCodesSearch} icon="/assets/images/search-icon.svg" placeholder={t('promoCode.search')} />
                              {/*filter*/}
                              <CustomDropdown
                                ref={filterSortByStatusDropRef}
                                isOpen={filterSortByStatusOpen}
                                onToggle={() => setFilterSortByStatusOpen(!filterSortByStatusOpen)}
                                options={filterSortByStatusOptions}
                                value={sortByStatus}
                                onChange={handleSortByStatusChange}
                                width="200px"
                                label={t('promoCode.status')}
                              />
                              <CustomDropdown
                                ref={filterSortByTypeDropRef}
                                isOpen={filterSortByTypeOpen}
                                onToggle={() => setFilterSortByTypeOpen(!filterSortByTypeOpen)}
                                options={filterSortByTypeOptions}
                                value={sortByType}
                                onChange={handleSortByTypeChange}
                                width="250px"
                                label={t('promoCode.type')}
                              />
                            </>
                          ) : (
                            <>
                              <label htmlFor="select-all-checkbox" className="fancy-checkbox flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  id="select-all-checkbox"
                                  checked={allItemsSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPromoCodeIds(fetchedData.data.map((item) => item._id));
                                    } else {
                                      setSelectedPromoCodeIds([]);
                                    }
                                  }}
                                />
                                <span className="box" />
                                <span>{t('promoCode.selectAll')}</span>
                              </label>

                              <button
                                className="primaryButton"
                                onClick={() => {
                                  setDeletePromoCodesModalOpen(true);
                                }}
                              >
                                {t('promoCode.delete')}
                              </button>

                              <button
                                className="primaryButton inverse"
                                onClick={() => {
                                  handleBulkChangeState();
                                }}
                              >
                                {t('promoCode.changeState')}
                              </button>
                            </>
                          )}
                          {/*filter*/}
                        </div>
                      </div>
                      <button type="button" className="primaryButton inverse" onClick={handleCSVExport}>
                        {t('promoCode.exportCsv')}{' '}
                      </button>
                    </div>
                    {/*filters*/}
                    {/*table*/}
                    <div className={styles.tableBody}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('promoCode.nameTh')}</th>
                              <th>{t('promoCode.type')}</th>
                              <th>{t('promoCode.plan')}</th>
                              <th>{t('promoCode.validityPeriodTh')}</th>
                              <th>{t('promoCode.status')}</th>
                              <th className={tableStyles.actionColumn}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fetchedData &&
                              fetchedData.data &&
                              fetchedData?.data
                                ?.filter((item: PromoCode) => !item.isDeleted)
                                ?.map((item: PromoCode) => {
                                  return (
                                    <tr key={item._id}>
                                      <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label htmlFor={item._id + 'checkbox'} className="fancy-checkbox flex items-start gap-2">
                                          <input
                                            type="checkbox"
                                            id={item._id + 'checkbox'}
                                            checked={selectedPromoCodeIds.includes(item._id)}
                                            onChange={() => setSelectedPromoCodeIds((prev) => (prev.includes(item._id) ? prev.filter((id) => id !== item._id) : [...prev, item._id]))}
                                          />
                                          <span className="box" />
                                        </label>
                                        {item.code}
                                      </td>
                                      <td>{item.promoCodeType}</td>
                                      <td>{item?.plans?.length > 1 ? `${item?.plans?.length} plans` : `${formatPlanName(item?.plans[0]?.product?.singleFlight, item?.plans[0]?.product?.family, item?.plans[0]?.product?.periodInMonths)}`}</td>
                                      <td style={{ display: 'flex', flexWrap: 'nowrap', width: '100%', maxWidth: '260px', justifyContent: 'space-between' }}>
                                        <span>
                                          {formatDate(item.validityPeriod.from)} <br /> {formatTime(item.validityPeriod.from)}
                                        </span>{' '}
                                        -{' '}
                                        <span>
                                          {formatDate(item.validityPeriod.to)} <br /> {formatTime(item.validityPeriod.to)}
                                        </span>
                                      </td>
                                      <td>{formatStatus(t, item.isActive ? 'active' : 'deactivated')}</td>
                                      <td className={tableStyles.actionColumn}>
                                        <div className={tableStyles.actionShape} ref={buttonRef}>
                                          <img
                                            src="/assets/images/dots.svg"
                                            alt="dots"
                                            onClick={(e) => {
                                              const buttonElement = e.currentTarget as HTMLDivElement;
                                              handleActionClick(item._id, buttonElement);
                                            }}
                                          />
                                          {isActionOpen !== null &&
                                            isActionOpen === item._id &&
                                            typeof window !== 'undefined' &&
                                            createPortal(
                                              <div
                                                ref={actionDropdownRef}
                                                className={`${tableStyles.actionShapeDropdown}`}
                                                style={{
                                                  top: `${dropdownPosition.top}px`,
                                                  left: `${dropdownPosition.left}px`,
                                                }}
                                              >
                                                <div>
                                                  <button
                                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                                    onClick={() => {
                                                      setIsActionOpen(null);
                                                      handleStartEditCode(item);
                                                    }}
                                                    className={tableStyles.actionShapeDropdownItem}
                                                  >
                                                    {t('promoCode.editCode')}
                                                  </button>
                                                </div>
                                                <div className={tableStyles.toggleContainer}>
                                                  <p>{t('promoCode.changeStatus')}</p>
                                                  <label className={tableStyles.toggleSwitch}>
                                                    <input
                                                      type="checkbox"
                                                      checked={item.isActive}
                                                      className={tableStyles.toggleInput}
                                                      onChange={() => {
                                                        handleChangePromoCodeStatus(item._id, !item.isActive);
                                                      }}
                                                    />
                                                    <span className={tableStyles.toggleSlider}></span>
                                                  </label>
                                                </div>
                                              </div>,
                                              document.body
                                            )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/*table*/}
                    {/*Pagination*/}
                    <Pagination onPrevious={onPrevious} onNext={onNext} currentPage={page} totalPages={fetchedData.pagination.totalPages} />
                    {/*Pagination*/}
                  </>
                )}
                {view === 'analytics' && (
                  <>
                    {/*header*/}
                    {/*statistics*/}
                    <div className={globalPageStyle.statistics}>
                      {/*TOTAL CLICKS*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('promoCode.numberOfUsedCoupons')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData.totalNumberOfUsages}</p>
                          </div>
                        </div>
                      </div>
                      {/*TOTAL CLICKS*/}
                      {/*PURCHASES THROUGH THE LINK*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('promoCode.totalDiscountAchieved')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>€{fetchedData.totalRevenue / 100}</p>
                          </div>
                        </div>
                      </div>
                      {/*PURCHASES THROUGH THE LINK*/}
                    </div>
                    {/*statistics*/}
                    {/*header*/}
                    {/*table*/}
                    <div className={styles.tableBody}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('promoCode.nameTh')}</th>
                              <th>{t('promoCode.type')}</th>
                              <th>{t('promoCode.plan')}</th>
                              <th>{t('promoCode.discountAchieved')}</th>
                              {/* <th>Conversion rate</th> */}
                              <th>{t('promoCode.used')}</th>
                              <th className={tableStyles.actionColumn}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fetchedData &&
                              fetchedData.data &&
                              fetchedData.data?.map((item: PromoCode) => {
                                return (
                                  <tr key={item._id}>
                                    <td>{item.code}</td>
                                    <td>{item.promoCodeType}</td>
                                    <td>{item?.plans?.length > 1 ? `${item?.plans?.length} ` + t('promoCode.plansTd') : `${formatPlanName(item?.plans[0]?.product?.singleFlight, item?.plans[0]?.product?.family, item?.plans[0]?.product?.periodInMonths)}`}</td>
                                    <td>€{Number((item.usageHistory.reduce((total, usage) => total + (usage.discountedAmount ?? 0), 0) ?? 0) / 100).toFixed(2)}</td>
                                    {/* <td>{item.conversionRate}</td> */}
                                    <td>{item.usageHistory.length}</td>
                                    {!item.isDeleted && (
                                      <td className={tableStyles.actionColumn}>
                                        <div className={tableStyles.actionShape} ref={buttonRef}>
                                          <img
                                            src="/assets/images/dots.svg"
                                            alt="dots"
                                            onClick={(e) => {
                                              const buttonElement = e.currentTarget as HTMLDivElement;
                                              handleActionClick(item._id, buttonElement);
                                            }}
                                          />
                                          {isActionOpen !== null &&
                                            isActionOpen === item._id &&
                                            typeof window !== 'undefined' &&
                                            createPortal(
                                              <div
                                                ref={actionDropdownRef}
                                                className={`${tableStyles.actionShapeDropdown}`}
                                                style={{
                                                  top: `${dropdownPosition.top}px`,
                                                  left: `${dropdownPosition.left}px`,
                                                }}
                                              >
                                                <div>
                                                  <button
                                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                                    onClick={() => {
                                                      handleStartEditCode(item);
                                                    }}
                                                    className={tableStyles.actionShapeDropdownItem}
                                                  >
                                                    {t('promoCode.editCode')}
                                                  </button>
                                                </div>
                                                <div className={tableStyles.toggleContainer}>
                                                  <p>{t('promoCode.changeStatus')}</p>
                                                  <label className={tableStyles.toggleSwitch}>
                                                    <input
                                                      type="checkbox"
                                                      checked={item.isActive}
                                                      className={tableStyles.toggleInput}
                                                      onChange={() => {
                                                        handleChangePromoCodeStatus(item._id, !item.isActive);
                                                      }}
                                                    />
                                                    <span className={tableStyles.toggleSlider}></span>
                                                  </label>
                                                </div>
                                              </div>,
                                              document.body
                                            )}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/*table*/}
                    {/*pagination*/}
                    <Pagination onPrevious={onPrevious} onNext={onNext} currentPage={page} totalPages={fetchedData.pagination.totalPages} />
                    {/*pagination*/}
                  </>
                )}
                {view === 'edit-promo-code' && (
                  <>
                    <form
                      className={styles.newPromoCodeContainer}
                      onSubmit={(e) => {
                        handleEditPromoCode(e);
                      }}
                    >
                      <p className={styles.newPromoCodeTitle}>Edit Promo Code</p>
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.name')}</p>
                          <CustomTextInput
                            placeholder="e.g., “welcome10”"
                            type="text"
                            id="name"
                            value={editingPromoCode?.code}
                            onChange={(e) => {
                              if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, code: e.target.value });
                            }}
                            className={styles.inputClass}
                            required
                          />
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.discountType')}</p>
                          <CustomDropdown
                            ref={discountTypeDropRef}
                            isOpen={discountTypeOpen}
                            onToggle={() => setDiscountTypeOpen(!discountTypeOpen)}
                            options={discountTypeOptions}
                            value={editingPromoCode ? (discountTypeOptions.find((item) => item.value === String(editingPromoCode.discountType)) ?? discountType) : discountType}
                            onChange={(item) => {
                              if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, discountType: Number(item.value) });
                              setDiscountType(item);
                            }}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.chooseValue')}
                          />
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.validityPeriod')}</p>
                          <CustomCalendar dateValues={dateValues} handleDateChange={handleDateChange} onCancel={handleDateOnCancel} ref={dateRangeRef} fullWidth fullHeight showHours />
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.codeType')}</p>
                          <CustomDropdown
                            ref={codeTypeDropRef}
                            isOpen={codeTypeOpen}
                            onToggle={() => setCodeTypeOpen(!codeTypeOpen)}
                            options={codeTypeOptions}
                            value={editingPromoCode ? (codeTypeOptions.find((item) => item.value === String(editingPromoCode.promoCodeType)) ?? codeType) : codeType}
                            onChange={(item) => {
                              if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, promoCodeType: item.value });
                              setCodeType(item);
                            }}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.chooseCodeType')}
                          />
                        </div>
                      </div>
                      {/* grid container*/}
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.restrictionsPerUser')}</p>
                          <CustomTextInput
                            placeholder="e.g., “1”"
                            type="number"
                            min={1}
                            id="restrictionsperuser"
                            value={editingPromoCode ? String(editingPromoCode.usagePerUser) : promoCodeRestrictionsPerUser}
                            onChange={(e) => {
                              if (e.target.value !== '' && Number(e.target.value) < 1) {
                                e.target.value = '1';
                                if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, usagePerUser: 1 });
                                setPromoCodeRestrictionsPerUser('1');
                              } else {
                                if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, usagePerUser: Number(e.target.value) });
                                setPromoCodeRestrictionsPerUser(e.target.value);
                              }
                            }}
                            className={styles.inputClass}
                            required
                          />
                        </div>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.restrictionsPerGlobalUsage')}</p>
                          <CustomTextInput
                            placeholder="e.g., “500”"
                            type="number"
                            min={1}
                            id="restrictionsglobal"
                            value={editingPromoCode ? String(editingPromoCode.globalUsageLimit) : promoCodeRestrictionsGlobal}
                            onChange={(e) => {
                              if (e.target.value !== '' && Number(e.target.value) < 1) {
                                e.target.value = '1';
                                if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, globalUsageLimit: 1 });
                                setPromoCodeRestrictionsGlobal('1');
                              } else {
                                if (editingPromoCode !== null) setEditingPromoCode({ ...editingPromoCode, globalUsageLimit: Number(e.target.value) });
                                setPromoCodeRestrictionsGlobal(e.target.value);
                              }
                            }}
                            className={styles.inputClass}
                            required
                          />
                        </div>
                      </div>
                      {/* grid container*/}
                      {/* grid container*/}
                      <div className={styles.gridContainer}>
                        <div className={styles.gridItem}>
                          <p className={styles.gridItemLabel}>{t('promoCode.plans')}</p>
                          <CustomDropdownSearch
                            ref={selectingPlansDropRef}
                            isOpen={selectingPlansOpen}
                            onToggle={() => setSelectingPlansOpen(!selectingPlansOpen)}
                            options={availablePlansOptions.map((plan) => plan.name)}
                            value={selectedPlans}
                            onChange={handleChangeSelectedPlans}
                            onSearch={handleSearchAvailablePlans}
                            width="100%"
                            labelClasses={styles.labelClass}
                            wrapperClasses={styles.headerWrapper}
                            headClasses={styles.headerWrapper}
                            placeholderStyle={styles.placeholderStyle}
                            placeholder={t('promoCode.chooseCodeType')}
                          />
                        </div>
                      </div>
                      {/* grid container*/}
                      <button type="submit" className="primaryButton fit">
                        {t('promoCode.editPromoCode')}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          )
        ) : (
          <div>{t('promoCode.noPermission')}</div>
        )}
      </div>
      <PromoCodeAddedModal
        isOpen={successfulModalOpen}
        onClose={() => {
          setSuccessfulModalOpen(false);
        }}
      />
      <PromoCodeDeletingModal
        isOpen={deletePromoCodesModalOpen}
        onClose={() => {
          setDeletePromoCodesModalOpen(false);
        }}
        onConfirm={() => {
          handleBulkDelete();
        }}
      />
    </main>
  );
};
export default PromoCodePage;
